/**
 * FLUX.2 on fal.ai (U7): flux-2-max, flux-2-pro, flux-2-turbo, flux-2-flash.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * Replaces (after the C5 cleanup): falai.ts async-image arm `case 'flux-2-*'` (:877-893) and the
 * sync arm (:391-408), ProviderService.generateImageFalAI's generic builder for these ids
 * (:1012-1035), and the FLUX price rows (migration 119; live model_registry values).
 *
 * P1 is payload- and price-neutral: every key legacy sends is reproduced, dead ones included
 * (R13), and the price is today's CreditService generic tail (ladder over `resolution`).
 *
 * Legacy wire, per surface (oracle snapshots 2026-09-19, __schemas__/fal-ai__flux-2*):
 *   both      prompt, image_size (preset from aspectRatio, square_hd fallback), num_images: 1,
 *             enable_safety_checker: false, output_format: 'png'
 *   editor    + num_inference_steps (QUALITY_STEPS[model][quality] || balanced || 8)
 *             + guidance_scale: 3.5
 *   headless  + negative_prompt when the node supplies one (generic builder)
 *   edit      + image_urls: every reference, NOT truncated (legacy sends all 12 of 12)
 *
 *   max / pro      num_images, num_inference_steps, guidance_scale, negative_prompt are all
 *                  ABSENT from Flux2{Max,Pro}Input and their edit inputs: dead keys fal drops.
 *   turbo / flash  num_images and guidance_scale are REAL schema fields (fal default guidance
 *                  2.5); num_inference_steps and negative_prompt are dead.
 *
 * MARGIN EXPOSURE IN EDIT MODE (owner decision, unchanged by P1): every vendor rate here is per
 * megapixel of INPUT as well as output — pro adds $0.015 per extra input MP, max counts input
 * images as processed MP at $0.03, turbo/flash charge their rate per input MP too. Legacy sends
 * every reference uncapped (the golden corpus has 12) and every legacy image path reserves a FLAT
 * 1 unit, so an edit's credits do not grow with the references. A 12-reference flux-2-pro edit
 * costs roughly $0.21 upstream against 14 credits (~$0.047): negative margin. The published
 * marginException lines below compare only a 1 MP t2i output.
 *
 * The flux-2-flash (and flux-2-turbo) headless guidance difference: the editor sends
 * guidance_scale 3.5 where headless sends nothing, so fal renders headless at its default 2.5.
 * On these two models that is a LIVE wire difference (the field exists), reproduced as an
 * editor-only legacy.wire entry with retire 'owner-approval' (Q6 converges headless). On max /
 * pro the same key is dead, so it is 'inert-cleanup' there (Q2). No U2 file is involved: the
 * descriptor's per-surface legacy.wire expresses it.
 */
import type { LegacyWire, ModeSpec, ModelDescriptor, Param, Sku } from '../types';
import { FAL_IMAGE_SIZE_PRESETS } from '../_shared';

type FluxTier = 'max' | 'pro' | 'turbo' | 'flash';

interface FluxSpec {
  tier: FluxTier;
  id: string;
  name: string;
  t2i: string;
  edit: string;
  /** Live production credits_per_unit (the ladder base). */
  base: number;
  /** QUALITY_STEPS row (falai.ts:80-95); absent -> the route's fallback 8 for every quality. */
  steps?: { fast: number; balanced: number; high: number };
  vendor: Sku['vendor'];
  marginException?: string;
}

/**
 * min 0, not 1: P1 reproduces legacy, and the editor route posts a mask-only body (maskUrl,
 * no imageUrl/imageUrls) to the EDIT endpoint with no image_urls — reserved, submitted, and
 * refused by fal afterwards (image_urls is required). A min of 1 would turn that into a
 * pre-reserve 400 the golden does not record (class A: legacy never refunded synchronously).
 * P2 raises it to 1 together with chooseMode (Q5).
 */
const refsPort = { slot: 'refs', kind: 'image', min: 0, max: 9, handle: 'input-image' } as const;

/** Parity-only wire, reproduced exactly as the legacy builders send it. */
function fluxLegacyWire(s: FluxSpec): LegacyWire[] {
  const real = s.tier === 'turbo' || s.tier === 'flash';
  const schemaName = real ? 'Flux2 dev turbo/flash input' : `Flux2${s.tier === 'max' ? 'Max' : 'Pro'}Input / EditInput`;
  const out: LegacyWire[] = [];
  if (!real) {
    out.push({ field: 'num_images', value: 1, why: `absent from ${schemaName} (oracle 2026-09-19); both surfaces send 1`, retire: 'inert-cleanup' });
  }
  out.push(
    s.steps
      ? { field: 'num_inference_steps', fromParam: 'quality', map: s.steps, surface: 'editor',
          why: `editor QUALITY_STEPS['${s.id}'] (falai.ts:80-95); absent from ${schemaName}`, retire: 'inert-cleanup' }
      : { field: 'num_inference_steps', value: 8, surface: 'editor',
          why: `editor QUALITY_STEPS has no '${s.id}' row, so every quality sends the route fallback 8; absent from ${schemaName}`,
          retire: 'inert-cleanup' },
    real
      ? { field: 'guidance_scale', value: 3.5, surface: 'editor',
          why: 'editor constant 3.5; headless omits it and fal renders its default 2.5 (a live surface difference, Q6)',
          retire: 'owner-approval' }
      : { field: 'guidance_scale', value: 3.5, surface: 'editor', why: `editor constant; absent from ${schemaName}`, retire: 'inert-cleanup' },
    { field: 'negative_prompt', fromParam: 'negativePrompt', surface: 'headless',
      why: `headless generic builder sends a truthy negativePrompt (ProviderService.ts:1025); absent from ${schemaName}`,
      retire: 'inert-cleanup' },
  );
  return out;
}

function fluxParams(): Param[] {
  return [
    { id: 'aspectRatio', type: 'enum', values: ['1:1', '9:16', '16:9', '4:3', '3:4', 'custom'], default: '1:1',
      invalid: { coerce: { kind: 'default' } },
      wire: { kind: 'imageSize', field: 'image_size', presets: FAL_IMAGE_SIZE_PRESETS, fallback: 'square_hd',
              custom: { whenValue: 'custom', widthParam: 'customWidth', heightParam: 'customHeight', enabled: false } },   // P2 enables
      ui: { label: 'Aspect ratio', tier: 'basic' } },
    { id: 'quality', type: 'enum', values: ['fast', 'balanced', 'high'], default: 'balanced', invalid: { coerce: { kind: 'default' } },
      wire: false, ui: { label: 'Quality', tier: 'hidden' } },   // only feeds the dead num_inference_steps (legacy.wire)
    { id: 'resolution', type: 'string', default: null, role: 'tier', invalid: 'passthrough', wire: false },   // billing-only ladder axis
    { id: 'negativePrompt', type: 'string', default: null, invalid: 'passthrough', wire: false },           // headless legacy.wire only
  ];
}

function flux(s: FluxSpec): ModelDescriptor {
  const real = s.tier === 'turbo' || s.tier === 'flash';
  const edit: ModeSpec = {
    endpoint: s.edit,
    ports: [refsPort],
    // No `max`: legacy converts and sends every reference (edge golden imageUrls-12 sends 12).
    media: { refs: { field: 'image_urls', many: true, encode: 'image' } },
  };
  return {
    id: s.id, provider: 'falai', face: 'image', displayName: s.name, ledgerName: s.name, status: 'live',
    prompt: { field: 'prompt', required: true },
    modes: {
      t2i: { endpoint: s.t2i, ports: [], media: {} },
      edit,
    },
    params: fluxParams(),
    fixed: real
      ? { num_images: 1, enable_safety_checker: false, output_format: 'png' }
      : { enable_safety_checker: false, output_format: 'png' },
    legacy: {
      wire: fluxLegacyWire(s),
      notes: [
        'Edit refs are not truncated (the FLUX 2 Pro edit port says max 9; legacy sends every reference).',
        'Editor mask-only bodies post to the edit endpoint with no image_urls (legacyImageMode); fal refuses them.',
      ],
    },
    pricing: {
      unit: 'generation', units: { fixed: 1 }, rounding: 'generic', policy: 'fal-image-35',
      skus: [{
        id: s.id, rate: { ladder: { base: s.base, by: 'resolution', curve: 'legacy-resolution-v1' } },
        vendor: s.vendor,
        ...(s.marginException ? { marginException: s.marginException } : {}),
        registry: { displayName: s.name, operationType: 'image', flatRate: false },
      }],
    },
    knowledge: {
      summary: real
        ? `FLUX.2 [dev] ${s.tier}: fast distilled model; up to 9 references in edit.`
        : `FLUX.2 [${s.tier}] by Black Forest Labs; up to 9 references in edit.`,
    },
    schemaVerified: '2026-09-19',
  };
}

const PLATFORM_API = 'fal Platform API GET /v1/models/pricing unit_price, 2026-09-19';

export const FLUX_2_MAX: ModelDescriptor = flux({
  tier: 'max', id: 'flux-2-max', name: 'FLUX 2 Max', t2i: 'fal-ai/flux-2-max', edit: 'fal-ai/flux-2-max/edit', base: 32,
  steps: { fast: 20, balanced: 30, high: 50 },
  vendor: { usd: 0.07, per: 'megapixel', field: 'pricingInfoOverride', source: `fal-ai/flux-2-max (${PLATFORM_API}: 0.07/megapixels)`,
            text: 'The first processed megapixel will cost **$0.07**. Each additional megapixel will cost **0.03**', verified: '2026-09-19' },
  marginException: 'live 32 (migration 119); policy ceil(0.07 x 300 / 0.65) = 33. P1 is price-neutral: owner decision',
});

export const FLUX_2_PRO: ModelDescriptor = flux({
  tier: 'pro', id: 'flux-2-pro', name: 'FLUX 2 Pro', t2i: 'fal-ai/flux-2-pro', edit: 'fal-ai/flux-2-pro/edit', base: 14,
  steps: { fast: 20, balanced: 30, high: 50 },
  vendor: { usd: 0.03, per: 'megapixel', field: 'pricingInfoOverride', source: `fal-ai/flux-2-pro (${PLATFORM_API}: 0.03/processed megapixels)`,
            text: 'Your request will cost **$0.03** for the first megapixel of output, plus **$0.015** per extra megapixel of input and output, rounded up to the nearest megapixel.',
            verified: '2026-09-19' },
});

export const FLUX_2_TURBO: ModelDescriptor = flux({
  tier: 'turbo', id: 'flux-2-turbo', name: 'FLUX 2 Turbo', t2i: 'fal-ai/flux-2/turbo', edit: 'fal-ai/flux-2/turbo/edit', base: 14,
  // The model page's first pricingInfoOverride ($0.012) belongs to fal-ai/flux-2/edit, not to this
  // endpoint (the page embeds its siblings); the structured per-endpoint price is the Platform API's.
  vendor: { usd: 0.008, per: 'megapixel', field: 'docs', source: `fal-ai/flux-2/turbo (${PLATFORM_API}: 0.008/megapixels; edit 0.008/processed megapixels)`,
            text: 'Requests cost **$0.008** per megapixel of input and output.', verified: '2026-09-19' },
  marginException: 'live 14 (migration 119); policy ceil(0.008 x 300 / 0.65) = 4. P1 is price-neutral: owner decision',
});

export const FLUX_2_FLASH: ModelDescriptor = flux({
  tier: 'flash', id: 'flux-2-flash', name: 'FLUX 2 Flash', t2i: 'fal-ai/flux-2/flash', edit: 'fal-ai/flux-2/flash/edit', base: 5,
  vendor: { usd: 0.005, per: 'megapixel', field: 'docs', source: `fal-ai/flux-2/flash (${PLATFORM_API}: 0.005/megapixels; edit 0.005/processed megapixels)`,
            text: 'Requests cost **$0.005** per megapixel of input and output.', verified: '2026-09-19' },
  marginException: 'live 5 (migration 119); policy ceil(0.005 x 300 / 0.65) = 3. P1 is price-neutral: owner decision',
});

/** Every FLUX.2 descriptor, in dropdown order (nodeDefinitions FluxImage). */
export const FLUX_MODELS: readonly ModelDescriptor[] = [FLUX_2_MAX, FLUX_2_PRO, FLUX_2_TURBO, FLUX_2_FLASH];
